/**
 * Phase 45 (2026-07-28) - Cumulative Net Area chart for the Cash Flow dashboard widget.
 *
 * Renders a single continuous cumulative-net line with:
 *   - Green fill between the line and the zero baseline WHERE the line is above zero.
 *   - Red fill between the line and the zero baseline WHERE the line is below zero.
 *
 * Recharts trick: two <Area> components with `dataKey` returning either the
 * positive or negative portion of `cumulative_net` and `baseValue={0}`. The
 * area draws between the line and the baseValue automatically.
 */
import { useMemo } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ReferenceLine,
} from "recharts";

const fmtCompact = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 1000) return `$${(v / 1000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
};
const fmtMoney = (n) => {
  const v = Number(n || 0);
  const sign = v < 0 ? "-" : "";
  return `${sign}$${Math.abs(v).toLocaleString("en-US", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
};

/** Tooltip renderer - module-scoped to avoid recharts re-renders. */
function CumulativeTooltip({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  // Both areas carry the same cumulative value; pick whichever is present.
  const cum = payload.find((p) => p.value !== null && p.value !== undefined);
  if (!cum) return null;
  return (
    <div
      className="rounded-md border p-2 text-xs shadow-md"
      style={{
        backgroundColor: "var(--bg-surface)",
        borderColor: "var(--border-default)",
        color: "var(--text-primary)",
      }}
    >
      <p className="font-medium mb-0.5" style={{ color: "var(--text-muted)" }}>
        {label}
      </p>
      <p>
        <span style={{ color: "var(--text-muted)" }}>Cumulative:</span>{" "}
        <span className="tabular-nums font-medium">{fmtMoney(cum.value)}</span>
      </p>
    </div>
  );
}

export default function CumulativeNetArea({ cumulativeSeries, finalValue, height = 260 }) {
  // Split each row into pos/neg fields for the two Area layers. `null` values
  // create a gap so the fill only draws in its own sign region.
  const data = useMemo(() => {
    if (!cumulativeSeries) return [];
    return cumulativeSeries.map((r) => ({
      date: r.date,
      pos: r.cumulative_net > 0 ? r.cumulative_net : (r.cumulative_net === 0 ? 0 : null),
      neg: r.cumulative_net < 0 ? r.cumulative_net : (r.cumulative_net === 0 ? 0 : null),
      cumulative_net: r.cumulative_net,
    }));
  }, [cumulativeSeries]);

  const finalPositive = Number(finalValue || 0) >= 0;
  const badgeColor = finalPositive ? "var(--success)" : "var(--danger)";
  const badgeSign = finalPositive ? "+" : "-";
  const badgeValue = `${badgeSign}$${Math.abs(Number(finalValue || 0)).toLocaleString(
    "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="relative" style={{ height }} data-testid="cumulative-net-area">
      {/* Cumulative net badge in the top-right corner of the chart area */}
      <div
        className="absolute top-1 right-3 text-right pointer-events-none"
        data-testid="cumulative-net-badge"
      >
        <p className="text-[10px] tracking-[0.22em] uppercase"
           style={{ color: "var(--text-muted)" }}>
          Cumulative Net
        </p>
        <p className="text-lg font-semibold tabular-nums leading-tight"
           style={{ color: badgeColor }}
           data-testid="cumulative-net-final">
          {badgeValue}
        </p>
      </div>

      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 30, right: 10, bottom: 0, left: -8 }}>
          <defs>
            <linearGradient id="gCumPos" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--success)" stopOpacity={0.30} />
              <stop offset="100%" stopColor="var(--success)" stopOpacity={0} />
            </linearGradient>
            <linearGradient id="gCumNeg" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--danger)" stopOpacity={0} />
              <stop offset="100%" stopColor="var(--danger)" stopOpacity={0.30} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-default)" vertical={false} />
          <XAxis
            dataKey="date"
            tick={{ fontSize: 10, fill: "var(--text-muted)" }}
            tickFormatter={(d) => (d ? d.slice(5) : "")}
            interval="preserveStartEnd"
            minTickGap={40}
          />
          <YAxis
            tick={{ fontSize: 10, fill: "var(--text-muted)" }}
            tickFormatter={fmtCompact}
            width={54}
          />
          <ReferenceLine y={0} stroke="var(--text-muted)" strokeOpacity={0.55} strokeWidth={1} />
          <Tooltip content={<CumulativeTooltip />} />
          {/* Green area WHEN cumulative >= 0. `baseValue={0}` fills the wedge to
              the zero baseline; positive values pin to top of chart. */}
          <Area
            type="monotone"
            dataKey="pos"
            stroke="none"
            fill="url(#gCumPos)"
            baseValue={0}
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* Red area WHEN cumulative < 0. */}
          <Area
            type="monotone"
            dataKey="neg"
            stroke="none"
            fill="url(#gCumNeg)"
            baseValue={0}
            isAnimationActive={false}
            connectNulls={false}
          />
          {/* Continuous navy line across the whole series (this is the outline). */}
          <Area
            type="monotone"
            dataKey="cumulative_net"
            stroke="var(--text-primary)"
            strokeWidth={1.5}
            fill="none"
            isAnimationActive={false}
            dot={false}
            activeDot={{ r: 3, fill: "var(--text-primary)" }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
